import { Tooltip } from '@material-ui/core'
import Button from '@material-ui/core/Button'
import Dialog from '@material-ui/core/Dialog'
import DialogActions from '@material-ui/core/DialogActions'
import DialogContent from '@material-ui/core/DialogContent'
import DialogTitle from '@material-ui/core/DialogTitle'
import { withStyles } from '@material-ui/core/styles'
import { Help } from '@material-ui/icons'
import Close from '@material-ui/icons/Close'
import styled, { ThemeProvider } from 'styled-components'

import theme from '../../utils/theme'
import { DeleteButton, PrimaryButton, SecondaryButton } from '../Button'
import ButtonWithLink from '../ButtonWithLink'
import TooltipComponent from '../Tooltip'

const ModalHeader = styled(DialogTitle)`
  && {
    padding: 10px 10px 10px 20px;
    > * {
      display: flex;
      align-items: center;
      justify-content: space-between;
      margin: 0;
    }
  }
`

const CloseButton = styled(Button)`
  && {
    padding: 8px 10px;
    min-width: auto;
  }
`

const ModalHeading = styled.div`
  font-family: ${props => props.theme.typography.defaultFont};
  font-size: ${props => props.theme.typography.fontSizeMed};
  font-weight: ${props => props.theme.typography.fontWeightBold};
  line-height: 1;
  color: ${`var(--gameday-body-headingColor, ${theme.body.headingColor})`};
  display: flex;
  align-items: center;
`
const ModalHeadingContent = styled.div`
  font-size: 1rem;
  display: flex;
  flex-direction: row;
  padding: 5px 5px 0 3px;
`

const DialogFooter = styled.div`
  padding: 5px 12px;
  border-top: 1px solid ${`var(--gameday-borders-borderColor, ${theme.borders.borderColor})`};
`

const ActionButtons = styled(DialogActions)`
  && {
    justify-content: space-between;
  }
`

const ButtonWrap = styled.div`
  button {
    margin: 0 5px;
  }
`

const FooterDisclaimer = styled.div`
  font-size: ${props => props.theme.typography.fontSizeSmaller};
  text-align: ${props => props.alignText || 'right'};
  padding: 0 10px 6px;
`

const styles = () => ({
  innerContainer: {
    minWidth: '360px',
  },
  topHeading: {
    padding: '20px',
  },
  topHeadingBorder: {
    borderBottom: '1px solid #ccc',
    position: 'sticky',
    top: '0',
    zIndex: '1',
    background: theme.colors.white,
  },
  crossIcon: {
    float: 'right',
    '&:hover': {
      cursor: 'pointer',
    },
  },
  bottomBar: {
    borderTop: '1px solid #ccc',
    padding: 10,
  },
  paper: {
    border: theme.borders.default,
    color: `var(--gameday-body-textColor, ${theme.body.textColor})`,
    [theme.breakpoints.up('lg')]: {
      '&.MuiDialog-paperWidthSm .field-label-col.MuiGrid-grid-lg-2': {
        maxWidth: '33%',
        flexBasis: '33%',
      },
      '&.MuiDialog-paperWidthSm .field-col.MuiGrid-grid-lg-10': {
        maxWidth: '66%',
        flexBasis: '66%',
      },
    },
  },
  content: {
    '& p': {
      marginTop: 0,
    },
    '& .field-label-col .MuiFormLabel-root.MuiInputLabel-root': {
      paddingRight: '9px',
    },
  },
  closeIcon: {
    color: `var(--gameday-buttons-actionButtonColor, ${theme.buttons.actionButtonColor})`,
  },
})

function handleClose(func, val) {
  func(val)
}

function handleSubmit(func, funcClose, onValidate, isSubmitWithOutClose) {
  const runClose = () => {
    func()
    if (!isSubmitWithOutClose) {
      funcClose()
    }
  }

  if (typeof onValidate === 'function') {
    const isValid = onValidate()
    if (isValid) {
      runClose()
    }
  } else {
    runClose()
  }
}

function Modal(props) {
  const {
    classes,
    okText,
    cancelText,
    customLeftButton,
    extraText,
    label,
    tooltip,
    tooltipText,
    isOpen,
    handleModalClose,
    handleModalSubmit,
    handleModalExtra,
    handleValidate,
    titleBottomBorder,
    isShowSaveButton,
    isShowBottomBar,
    isHideCancel,
    isError,
    midWidthPopup,
    fullScreen,
    fullWidth,
    headerBackgroundcolor,
    headerTextColor,
    isSubmitWithOutClose,
    isHideExtra,
    customStyle,
    disableSaveButton,
    saveButtonClassName,
    extraButtonClassName,
    children,
    isCustomButtonColour,
    buttonColour,
    buttonTextColour,
    isShowSecondRightButton,
    customSecondButton,
    secondButtonText,
    handleSecondButton,
    disableSecondButton,
    isShowDeleteButton,
    deleteButtonClassName,
    disableDeleteButton,
    handleDeleteButton,
    deleteButtonText,
    okId,
    footerDisclaimer,
    footerDisclaimerAlign = 'right',
    ...rest
  } = props
  return (
    <ThemeProvider theme={theme}>
      <div className={classes.root}>
        <Dialog
          open={isOpen}
          disableBackdropClick
          aria-labelledby="alert-dialog-title"
          aria-describedby="alert-dialog-description"
          onClose={() => {
            handleClose(handleModalClose, isOpen)
          }}
          fullWidth={fullWidth}
          fullScreen={fullScreen}
          maxWidth={midWidthPopup ? 'md' : 'sm'}
          PaperProps={{
            style: customStyle,
            classes: {
              root: classes.paper,
            },
          }}
          {...rest}
        >
          <ModalHeader>
            <ModalHeading>
              {label}
              {tooltip && (
                <ModalHeadingContent>
                  <TooltipComponent content={tooltipText} placement="right">
                    <Help style={{ fontSize: '15px' }} />
                  </TooltipComponent>
                </ModalHeadingContent>
              )}
            </ModalHeading>
            <Tooltip title="Close" placement="left">
              <CloseButton
                onClick={() => {
                  handleClose(handleModalClose, isOpen)
                }}
                size="small"
                aria-label="Close"
                data-testid="closeModalButton"
              >
                <Close fontSize="small" className={classes.closeIcon} />
              </CloseButton>
            </Tooltip>
          </ModalHeader>
          <DialogContent classes={{ root: classes.content }}>{children}</DialogContent>
          {isShowBottomBar && (
            <DialogFooter>
              <ActionButtons>
                <ButtonWrap>
                  {!isHideExtra && (
                    <SecondaryButton className={extraButtonClassName} onClick={handleModalExtra} aria-label={extraText}>
                      {extraText}
                    </SecondaryButton>
                  )}
                  {customLeftButton && customLeftButton}
                </ButtonWrap>
                <ButtonWrap>
                  {!isHideCancel && (
                    <SecondaryButton
                      onClick={() => {
                        handleClose(handleModalClose, isOpen)
                      }}
                      aria-label={cancelText}
                      id="confirmation-cancel"
                      data-testid="cancelModalButton"
                    >
                      {cancelText}
                    </SecondaryButton>
                  )}
                  {customSecondButton && customSecondButton}
                  {!customSecondButton && isShowSecondRightButton && (
                    <PrimaryButton
                      className={saveButtonClassName}
                      disabled={disableSecondButton}
                      onClick={() => {
                        handleSubmit(handleSecondButton, handleModalClose, handleValidate, isSubmitWithOutClose)
                      }}
                      aria-label={secondButtonText}
                    >
                      {secondButtonText}
                    </PrimaryButton>
                  )}
                  {isShowDeleteButton && (
                    <DeleteButton
                      className={deleteButtonClassName}
                      disabled={disableDeleteButton}
                      onClick={() => {
                        handleSubmit(handleDeleteButton, handleModalClose, handleValidate, isSubmitWithOutClose)
                      }}
                      aria-label={deleteButtonText}
                    >
                      {deleteButtonText}
                    </DeleteButton>
                  )}
                  {isShowSaveButton &&
                    (isCustomButtonColour ? (
                      <ButtonWithLink
                        onClick={() => {
                          handleSubmit(handleModalSubmit, handleModalClose, handleValidate, isSubmitWithOutClose)
                        }}
                        buttoncolour={buttonColour}
                        buttontextcolour={buttonTextColour}
                        label={okText}
                        id={okId}
                      />
                    ) : (
                      <PrimaryButton
                        className={saveButtonClassName}
                        disabled={disableSaveButton}
                        onClick={() => {
                          handleSubmit(handleModalSubmit, handleModalClose, handleValidate, isSubmitWithOutClose)
                        }}
                        aria-label={okText}
                        id={okId}
                        data-testid="saveModalButton"
                      >
                        {okText}
                      </PrimaryButton>
                    ))}
                </ButtonWrap>
              </ActionButtons>
              {footerDisclaimer && (
                <FooterDisclaimer alignText={footerDisclaimerAlign}>{footerDisclaimer}</FooterDisclaimer>
              )}
            </DialogFooter>
          )}
        </Dialog>
      </div>
    </ThemeProvider>
  )
}

Modal.defaultProps = {
  okText: 'Save',
  okId: '',
  cancelText: 'Cancel',
  extraText: '',
  isOpen: false,
  titleBottomBorder: true,
  isShowBottomBar: false,
  isShowSaveButton: false,
  isHideCancel: false,
  isHideExtra: true,
  isError: false,
  label: '',
  midWidthPopup: false,
  fullScreen: false,
  fullWidth: true,
  isSubmitWithOutClose: false,
  disableSaveButton: false,
  saveButtonClassName: '',
  isShowSecondRightButton: false,
  secondButtonText: '',
  handleSecondButton: () => {},
  disableSecondButton: false,
}

const ModalComponent = withStyles(styles)(Modal)
ModalComponent.displayName = 'Modal'
export default ModalComponent
